/**
 * fuzzy-text.js — React Bits FuzzyText 的 vanilla ES module 移植（精簡版）
 *
 * 用途：把一段純文字標題渲染成 canvas，每幀逐 row 做水平隨機位移，產生「掃描雜訊／
 * 模糊抖動」質感；hover 落在文字 bbox 內時提高抖動強度。404 巨數字「404」專用，
 * 但設計成可重用於任何標題元素。
 *
 * 移植自 React Bits FuzzyText 的核心演算法，但刻意只保留 horizontal 方向（設計基調乾淨非
 * 華麗）：vertical / both / glitch / click / gradient / letterSpacing 全部不做。
 *
 * 關鍵工程點：
 *   1. 量測前必 await document.fonts.load(fontString)：Outfit 未載完時 measureText 量到的是
 *      fallback 字寬，整段 bounding box 全錯 → 文字被裁。
 *   2. 外觀全繼承：fontSize / fontWeight / fontFamily / color 一律從「目標元素的 computedStyle」
 *      讀（getComputedStyle），不 hardcode。元件預設白字在暖紙底會隱形，務必繼承 ink 色。
 *   3. DPR：canvas backing store × devicePixelRatio + ctx.scale，CSS 尺寸鎖原本 px，否則 retina
 *      上標題級文字會糊（糊＝像破圖）。
 *   4. prefers-reduced-motion: reduce → 完全不初始化，保留原純文字標題（最穩做法，呼叫端負責）。
 *   5. a11y：canvas aria-hidden，原標題文字以 visually-hidden 保留給螢幕閱讀器（呼叫端負責）。
 *
 * 用法：
 *   import { initFuzzyText } from './components/fuzzy-text.js';
 *   const handle = initFuzzyText(targetEl, { text: '404', mount: canvasEl });
 *   // handle.destroy() 停 rAF + 解除事件（呼叫端在 pagehide 時呼叫）
 *
 * 回傳 null 的情況：PRM 啟用、target 缺失、或 2D context 取不到（呼叫端據此保留純文字）。
 */

// P-H-3：改 import 共用 prefersReducedMotion()（utils/dom.js），去重原 inline matchMedia 判斷。
import { prefersReducedMotion } from '../utils/dom.js';

/* ===== 參數定值（規格指定，集中常數，避免散落 magic number）===== */
const BASE_INTENSITY = 0.2;
const HOVER_INTENSITY = 0.5;
const ENABLE_HOVER = true;
const FUZZ_RANGE = 30; // 每 row 水平位移振幅上限（px）
const FPS = 60;
const FRAME_INTERVAL = 1000 / FPS;
const MEASURE_BUFFER = 10; // offscreen 寬度 buffer（規格指定 10px）
const MAIN_PAD = FUZZ_RANGE + 20; // 主 canvas 左右各留的位移餘裕（規格：2*(fuzzRange+20)）

const PRM = prefersReducedMotion();

/**
 * 初始化 fuzzy text 特效。
 *
 * @param {HTMLElement} targetEl 外觀來源元素（讀 computedStyle 取 font / color）
 * @param {object} opts
 * @param {string} opts.text   要渲染的文字（如 '404'）
 * @param {HTMLCanvasElement} opts.mount 已存在於 DOM 的 canvas（外觀繼承需 target 已套樣式）
 * @returns {{ destroy: () => void } | null} PRM / 失敗時回 null
 */
export function initFuzzyText(targetEl, { text, mount } = {}) {
  // PRM：完全不初始化，呼叫端保留純文字標題
  if (PRM) return null;
  if (!targetEl || !mount || !text) return null;

  const canvas = mount;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null; // 無 2D context（極舊環境）→ 保留純文字

  // ----- 外觀全繼承自 target computedStyle（禁 hardcode 色 / 字）-----
  const cs = window.getComputedStyle(targetEl);
  const fontSize = cs.fontSize; // 已是解析後 px 字串（clamp 在此被解成具體值）
  const fontWeight = cs.fontWeight;
  const fontFamily = cs.fontFamily;
  const color = cs.color;
  const fontString = `${fontWeight} ${fontSize} ${fontFamily}`;

  const dpr = window.devicePixelRatio || 1;

  let rafId = null;
  let lastFrameTime = 0;
  let intensity = BASE_INTENSITY;
  let targetIntensity = BASE_INTENSITY;
  let destroyed = false;

  // 文字 bbox（主 canvas 內部座標，含 translate 後的命中區），hover hit-test 用
  let textBox = { x: 0, y: 0, width: 0, height: 0 };
  // offscreen 一次性渲染好的文字位圖（每幀逐 row drawImage 取樣來源）
  let offscreen = null;
  let offW = 0;
  let offH = 0;

  /**
   * 量測 + 離屏渲染：await 字體載入 → measureText 取 actualBoundingBox → offscreen fillText。
   * 必須等字體，否則量到 fallback 字寬。
   */
  async function setup() {
    // Outfit 等自訂字體必須載完才量；load 失敗（離線等）仍續跑，measureText 退回可用字體
    try {
      await document.fonts.load(fontString);
    } catch {
      /* 字體載入失敗不阻斷：用當前可用字體量，視覺退化但不裂 */
    }
    if (destroyed) return;

    // ----- 量測：offscreen 尺寸 = 文字 bounding + buffer -----
    ctx.font = fontString;
    const m = ctx.measureText(text);
    const left = m.actualBoundingBoxLeft ?? 0;
    const right = m.actualBoundingBoxRight ?? m.width;
    const ascent = m.actualBoundingBoxAscent ?? parseInt(fontSize, 10);
    const descent = m.actualBoundingBoxDescent ?? 0;

    const textWidth = Math.ceil(left + right);
    const textHeight = Math.ceil(ascent + descent);

    offW = textWidth + MEASURE_BUFFER;
    offH = textHeight;

    // ----- offscreen：畫一次文字（baseline alphabetic，y = ascent）-----
    offscreen = document.createElement('canvas');
    offscreen.width = offW;
    offscreen.height = offH;
    const offCtx = offscreen.getContext('2d');
    offCtx.font = fontString;
    offCtx.textBaseline = 'alphabetic';
    offCtx.fillStyle = color; // 繼承 target ink 色
    // 起筆 x 補上 left bearing，避免左側字緣被裁
    offCtx.fillText(text, left, ascent);

    // ----- 主 canvas：寬 = offscreen 寬 + 2*MAIN_PAD，高 = offscreen 高 -----
    const mainW = offW + MAIN_PAD * 2;
    const mainH = offH;

    // DPR：backing store 放大，CSS 尺寸鎖原 px（retina 不糊）
    canvas.width = Math.ceil(mainW * dpr);
    canvas.height = Math.ceil(mainH * dpr);
    canvas.style.width = `${mainW}px`;
    canvas.style.height = `${mainH}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // 等價 scale(dpr)，但每幀重設不疊加
    ctx.translate(MAIN_PAD, 0);

    // 文字命中區（主 canvas 內部、未含 DPR 的 CSS 座標）：translate 後文字佔 [0..offW]
    textBox = { x: MAIN_PAD, y: 0, width: offW, height: offH };

    // 事件接線（量測完成後才綁，確保 textBox 已定）
    if (ENABLE_HOVER) bindHover(mainW, mainH);

    rafId = requestAnimationFrame(render);
  }

  /**
   * rAF loop：fps 節流；每幀 clearRect 後逐 row 水平位移 drawImage。
   * @param {number} ts requestAnimationFrame timestamp
   */
  function render(ts) {
    if (destroyed) return;

    // fps 節流：未到間隔只排下一幀、不重畫
    if (ts - lastFrameTime < FRAME_INTERVAL) {
      rafId = requestAnimationFrame(render);
      return;
    }
    lastFrameTime = ts;

    // intensity 平滑趨近 target（hover 進出柔順，非硬跳）
    intensity += (targetIntensity - intensity) * 0.2;

    // clearRect 用「未 translate」的全域座標：左移 MAIN_PAD 蓋掉左側位移殘影
    ctx.clearRect(-MAIN_PAD, 0, offW + MAIN_PAD * 2, offH);

    // 逐 row（CSS px 單位，配合 ctx scale(dpr)）水平位移取樣 offscreen
    for (let j = 0; j < offH; j++) {
      const dx = Math.floor(intensity * (Math.random() - 0.5) * FUZZ_RANGE);
      // 來源：offscreen 第 j 列整條；目的：主 canvas 同列、x 位移 dx
      ctx.drawImage(offscreen, 0, j, offW, 1, dx, j, offW, 1);
    }

    rafId = requestAnimationFrame(render);
  }

  /* ===== hover 互動：mousemove 命中 textBox → 切 targetIntensity ===== */
  let onMove = null;
  let onLeave = null;

  function bindHover(mainW, mainH) {
    // 換算指標座標到 canvas 內部座標（canvas 可能被 CSS 縮放，用 rect 比例還原）
    const hitTest = (clientX, clientY) => {
      const rect = canvas.getBoundingClientRect();
      // rect 寬高對應 CSS 尺寸 mainW/mainH；比例換算回內部 px
      const scaleX = mainW / rect.width;
      const scaleY = mainH / rect.height;
      const x = (clientX - rect.left) * scaleX;
      const y = (clientY - rect.top) * scaleY;
      return (
        x >= textBox.x &&
        x <= textBox.x + textBox.width &&
        y >= textBox.y &&
        y <= textBox.y + textBox.height
      );
    };

    onMove = (e) => {
      const point = e.touches ? e.touches[0] : e;
      if (!point) return;
      targetIntensity = hitTest(point.clientX, point.clientY)
        ? HOVER_INTENSITY
        : BASE_INTENSITY;
    };
    onLeave = () => {
      targetIntensity = BASE_INTENSITY;
    };

    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    // 觸控：touchmove 換算同 mousemove，touchend 還原
    canvas.addEventListener('touchmove', onMove, { passive: true });
    canvas.addEventListener('touchend', onLeave);
  }

  function destroy() {
    destroyed = true;
    if (rafId != null) cancelAnimationFrame(rafId);
    if (onMove) {
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('touchmove', onMove);
    }
    if (onLeave) {
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('touchend', onLeave);
    }
  }

  // 啟動（async setup，不阻塞回傳；destroy 內 destroyed 旗標保證 setup 中途取消安全）
  setup();

  return { destroy };
}
